import { useMemo } from 'react';
import { addDays, isBefore, isEqual } from 'date-fns';

/**
 * 날짜 범위의 유효성을 검증하고 보정하는 커스텀 훅
 *
 * @description
 * - startDate가 없으면 오늘 날짜로 설정
 * - endDate가 없거나 startDate보다 이르거나 같으면 startDate + 1일로 설정
 * - 비교 시 시분초를 포함하여 정확하게 검증
 *
 * @param startDateProp - 시작 날짜 (선택적)
 * @param endDateProp - 종료 날짜 (선택적)
 * @param allowEqualDates - true이면 start === end를 허용해 종료를 +1일로 보정하지 않습니다. (null·역전만 보정)
 * @returns 검증 및 보정된 시작/종료 날짜
 */
export const useDateRangeValidation = (
  startDateProp: Date | null | undefined,
  endDateProp: Date | null | undefined,
  allowEqualDates = false,
) => {
  const { validatedStartDate, validatedEndDate } = useMemo(() => {
    // startDate가 없으면 오늘 날짜로 설정
    const start = startDateProp || new Date();

    // endDate가 없거나 startDate보다 이르거나 같으면 startDate + 1일로 설정
    let end = endDateProp;
    if (!end || isBefore(end, start) || (!allowEqualDates && isEqual(end, start))) {
      end = addDays(start, 1);
    }

    return { validatedStartDate: start, validatedEndDate: end };
  }, [startDateProp, endDateProp, allowEqualDates]);

  return { validatedStartDate, validatedEndDate };
};
